import React from 'react'
import styled from '@emotion/styled'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { Drawer } from '@mobi/component-library/Overlays/Drawer'
import { BetSlip as BetSlipComponent } from '@mobi/betslip/App'
import { setHostHelpers } from '@mobi/betslip/helpers/hostHelpers'
import { setWorkflowStatusToReady } from '@mobi/betslip/Store/Workflow'
import { removeItemsWithFatalError, removeItemsWithReceipt } from '@mobi/betslip/Store/Bets'
import { AppRoutes } from '@core/AppRoutes'
import { startListening } from '@core/Store/listenerMiddleware'
import { useAppDispatch, useAppSelector } from '@core/Store/hooks'
import { openDepositModal } from '@core/Areas/Deposit/openDeposit'
import { appHistory } from '@core/Utils/routing/history'
import { useFeature } from '@core/Utils/hooks'
import { doesClientSupportShare as canShareBet } from '@core/Areas/ShareBet/helpers/doesClientSupportShare'
import { setBetSlipIsOpen } from '../AppHeader/Store'
import { HeaderPanel } from './Components/HeaderPanel'
import { LowBalanceStatus } from './Components/LowBalanceStatus'
import { TakeABreakStatus } from './Components/TakeABreakStatus'
import { InducementSync } from './Components/InducementSync'
import { postAllBetsToServer } from './helpers/postAllBetsToServer'
import { handleShareBet, showLogin, toggleBetSlipDrawer } from './helpers'
import {
  useRealtimeFobPriceChanges,
  useRefreshBalanceOnBetsPlaced,
  useAccountBalanceSync,
  useBetSlipAnalytics,
  useHapticFeedbackOnBetsSuccess,
  useAccountCampaignsSync,
  useToastOnClearBetSlipSuccess,
  useNativeNavCloseBetSlip,
  useSetQualtricsOnBetPlaced,
} from './hooks'
import { BonusBetSplittingTray, useSplitBonusBetMutation } from '../Bonuses'
import { groupBonusBets, isCampaignV2 } from '../Bonuses/data/helpers'
import { trackBonusOfferClick } from '../Bonuses/analytics'

setHostHelpers({
  history: appHistory,
  postAllBetsToServer,
  toggleBetSlipDrawer,
  handleShareBet,
  openDepositModal: () => openDepositModal('quick-deposit'),
  canShareBet,
  redux: { startListening },
  hostRoutes: {
    Meetings: AppRoutes.Meetings,
    Sports: AppRoutes.Sports,
  },
  showLogin,
  isReactNativeApp,
  useFeature,
  BonusBetSplittingTray,
  groupBonusBets,
  useSplitBonusBetMutation,
  isCampaignV2,
  trackBonusOfferClick,
})

export const BetSlip: React.FC = () => {
  const dispatch = useAppDispatch()

  const isOpen = useAppSelector(state => state.appHeader.isBetSlipOpen)

  useRealtimeFobPriceChanges()
  useRefreshBalanceOnBetsPlaced()
  useAccountBalanceSync()

  useToastOnClearBetSlipSuccess(isOpen)
  useHapticFeedbackOnBetsSuccess(isOpen)
  useNativeNavCloseBetSlip(isOpen)
  useAccountCampaignsSync(isOpen)
  useBetSlipAnalytics(isOpen)
  useSetQualtricsOnBetPlaced(isOpen)

  React.useEffect(() => {
    if (isOpen) return
    // Reset state on close
    dispatch(removeItemsWithFatalError())
    dispatch(setWorkflowStatusToReady())
    dispatch(removeItemsWithReceipt())
  }, [dispatch, isOpen])

  return (
    <Drawer
      isOpen={isOpen}
      shouldUseBodyScrollLock
      onDismiss={() => dispatch(setBetSlipIsOpen(false))}
    >
      <InducementSync />

      <TakeABreakStatus />

      <HeaderPanel />

      <LowBalanceStatus />

      <BetSlipParentWrapperStyled>
        <BetSlipComponent />
      </BetSlipParentWrapperStyled>
    </Drawer>
  )
}

// ======
// Styles
// ======

const BetSlipParentWrapperStyled = styled.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
})
